import {
  type CodePreference,
  codeableConceptLabel,
  codeableConceptTextOrDisplay,
  findAllCodings,
  findAllCodingsWithEnrichment,
  findCoding,
  findCodingByOrderOfPreference,
  findCodingWithEnrichment,
} from '@ctw/shared/api/fhir/codeable-concept';
import {
  getAddConditionWithDefaults,
  getClincalAndVerificationStatus,
} from '@ctw/shared/content/forms/actions/conditions';
import { formatFHIRDate } from '@ctw/shared/utils/dates';
import type { CodeableConcept, Coding, Condition } from 'fhir/r4';
import { capitalize, compact, find, intersectionWith, uniqWith } from 'lodash-es';
import { CCSChapterName } from '../mappings/ccs-chapter-names';
import { findReference } from '../resource-helper';
import {
  SYSTEM_CCI,
  SYSTEM_CCS,
  SYSTEM_CMS_HCC,
  SYSTEM_CONDITION_CLINICAL,
  SYSTEM_CONDITION_VERIFICATION_STATUS,
  SYSTEM_ICD9,
  SYSTEM_ICD9_CM,
  SYSTEM_ICD10,
  SYSTEM_ICD10_CM,
  SYSTEM_SNOMED,
} from '../system-urls';
import { FHIRModel } from './fhir-model';
import { PatientModel } from './patient';
import { PractitionerModel } from './practitioner';

type VerificationStatus =
  | 'unconfirmed'
  | 'provisional'
  | 'differential'
  | 'confirmed'
  | 'refuted'
  | 'entered-in-error';

type ClinicalStatus = 'active' | 'recurrence' | 'relapse' | 'inactive' | 'remission' | 'resolved';

const CONDITION_CODE_PREFERENCE_ORDER: Array<CodePreference> = [
  { system: SYSTEM_SNOMED, checkForEnrichment: true },
  { system: SYSTEM_ICD10, checkForEnrichment: true },
  { system: SYSTEM_SNOMED },
  { system: SYSTEM_ICD10 },
  { system: SYSTEM_ICD10_CM },
  { system: SYSTEM_ICD9 },
  { system: SYSTEM_ICD9_CM },
];

export function getNewCondition(patientId: string) {
  const newCondition: Condition = {
    resourceType: 'Condition',
    subject: {
      type: 'Patient',
      reference: `Patient/${patientId}`,
    },
    ...getClincalAndVerificationStatus('Active'),
  };
  return getAddConditionWithDefaults(newCondition);
}

export class ConditionModel extends FHIRModel<Condition> {
  public kind = 'Condition' as const;

  // Used by summary conditions to indicate that there's a matching
  // builder condition for this summary condition.
  public syncedWithRecord = false;

  public get abatement(): string | undefined {
    if (this.resource.abatementAge) {
      return this.resource.abatementAge.value?.toString();
    }

    if (this.resource.abatementDateTime) {
      return formatFHIRDate(this.resource.abatementDateTime);
    }

    if (this.resource.abatementPeriod) {
      return this.resource.abatementPeriod.start
        ? formatFHIRDate(this.resource.abatementPeriod.start)
        : undefined;
    }

    if (this.resource.abatementRange) {
      const preFormatted = this.resource.abatementRange.low?.value?.toString();
      return preFormatted ? formatFHIRDate(preFormatted) : undefined;
    }

    return this.resource.abatementString;
  }

  public get active(): boolean {
    const coding = find(this.resource.clinicalStatus?.coding, {
      system: SYSTEM_CONDITION_CLINICAL,
    });

    return coding?.code ? ['active', 'recurrence', 'relapse'].includes(coding.code) : false;
  }

  public get asserter(): string | undefined {
    return this.resource.asserter?.display;
  }

  public get bodySites(): Array<string> {
    return this.resource.bodySite?.map((bodySite) => codeableConceptLabel(bodySite)) || [];
  }

  // Returns the first category text or display.
  public get category(): string {
    return (
      compact(
        this.resource.category?.map((category) => codeableConceptTextOrDisplay(category)),
      )[0] ?? ''
    );
  }

  public get ccsChapter(): string | undefined {
    const code = this.ccsChapterCode;
    if (!code) {
      return 'Uncategorized';
    }
    return CCSChapterName[code]?.shortName;
  }

  public get ccsChapterCode(): string | undefined {
    return findCoding(SYSTEM_CCS, this.resource.code)?.code?.slice(0, 3);
  }

  public get ccsGrouping(): string | undefined {
    return findCoding(SYSTEM_CCS, this.resource.code)?.display;
  }

  public get clinicalStatus(): string {
    return codeableConceptLabel(this.resource.clinicalStatus);
  }

  public get clinicalStatusCode(): ClinicalStatus | undefined {
    return find(this.resource.clinicalStatus?.coding, {
      system: SYSTEM_CONDITION_CLINICAL,
    })?.code as ClinicalStatus | undefined;
  }

  public get codings(): CodeableConcept | undefined {
    return this.resource.code;
  }

  public get chronic(): Coding | undefined {
    if (this.isSummaryResource) {
      return findCoding(SYSTEM_CCI, this.resource.code);
    }
    return findCodingWithEnrichment(SYSTEM_CCI, this.resource.code);
  }

  public get display(): string | undefined {
    return capitalize(
      findCodingByOrderOfPreference(CONDITION_CODE_PREFERENCE_ORDER, this.resource.code)?.display ??
        codeableConceptLabel(this.resource.code),
    );
  }

  public get title() {
    return this.display ?? 'Condition';
  }

  public get type() {
    const type = findCoding(SYSTEM_CCI, this.resource.code)?.display || 'Unknown';
    return type.toLocaleLowerCase() === 'both' ? 'Chronic and acute' : type;
  }

  // Standardize the type display as to not end up with so many useless type categories to filter on
  public get typeDisplay() {
    const type = findCoding(SYSTEM_CCI, this.resource.code);
    const display = this.type;

    if (
      ['UNK', 'U', 'NULL', 'N'].includes(type?.code ?? '') ||
      /^(not applicable|unknown)/i.test(display)
    ) {
      return 'Unknown, not applicable';
    }

    return display;
  }

  public get encounter(): string | undefined {
    return this.resource.encounter?.display;
  }

  public get hccEnrichment(): Coding | undefined {
    if (this.isSummaryResource) {
      return findCoding(SYSTEM_CMS_HCC, this.resource.code);
    }
    return findCodingWithEnrichment(SYSTEM_CMS_HCC, this.resource.code);
  }

  public get hccEnrichments(): Array<Coding> {
    if (this.isSummaryResource) {
      return findAllCodings(SYSTEM_CMS_HCC, this.resource.code);
    }
    return findAllCodingsWithEnrichment(SYSTEM_CMS_HCC, this.resource.code);
  }

  public get icd10CMEnrichments(): Array<Coding> {
    if (this.isSummaryResource) {
      return findAllCodings(SYSTEM_ICD10_CM, this.resource.code);
    }
    return findAllCodingsWithEnrichment(SYSTEM_ICD10_CM, this.resource.code);
  }

  public get knownCodings(): Array<Coding> {
    return knownCodings(this.resource.code);
  }

  // Returns true if any of the known codings match between
  // this condition and the passed in condition or object with select fields from condition.
  public knownCodingsMatch(code?: CodeableConcept): boolean {
    return (
      intersectionWith(
        this.knownCodings,
        knownCodings(code),
        (a, b) => a.code === b.code && a.system === b.system,
      ).length > 0
    );
  }

  public get notes(): Array<string> {
    return this.resource.note?.map((note) => note.text) || [];
  }

  public get onset(): string | undefined {
    if (this.resource.onsetAge) {
      return this.resource.onsetAge.value?.toString();
    }

    if (this.resource.onsetDateTime) {
      return formatFHIRDate(this.resource.onsetDateTime);
    }

    if (this.resource.onsetPeriod) {
      return this.resource.onsetPeriod.start
        ? formatFHIRDate(this.resource.onsetPeriod.start)
        : undefined;
    }

    if (this.resource.onsetRange) {
      const preFormatted = this.resource.onsetRange.low?.value?.toString();
      return preFormatted ? formatFHIRDate(preFormatted) : undefined;
    }

    return this.resource.onsetString ? formatFHIRDate(this.resource.onsetString) : undefined;
  }

  public get patientOrganizationName(): string | undefined {
    const reference = findReference(
      'Patient',
      this.resource.contained,
      this.includedResources,
      this.resource.subject,
    );

    if (reference) {
      return new PatientModel(reference, this.includedResources).organizationDisplayName;
    }

    return undefined;
  }

  public get preferredCoding(): Coding | undefined {
    return findCodingByOrderOfPreference(CONDITION_CODE_PREFERENCE_ORDER, this.resource.code);
  }

  public get recordedDate(): string | undefined {
    return this.resource.recordedDate ? formatFHIRDate(this.resource.recordedDate) : undefined;
  }

  public get recorded(): string | undefined {
    return this.resource.recordedDate ? formatFHIRDate(this.resource.recordedDate) : undefined;
  }

  public get recorder(): string | undefined {
    const reference = this.resource.recorder;

    const practitioner = findReference(
      'Practitioner',
      this.resource.contained,
      this.includedResources,
      reference,
    );

    if (practitioner) {
      return new PractitionerModel(practitioner).fullName;
    }

    return this.resource.recorder?.display;
  }

  public get severity(): string {
    return codeableConceptLabel(this.resource.severity);
  }

  public get stages(): Array<string> {
    return (
      this.resource.stage?.map((stage) => {
        const summary = codeableConceptLabel(stage.summary);
        const type = codeableConceptLabel(stage.type);
        return `Summary: ${summary}, Type: ${type}`;
      }) || []
    );
  }

  public get displayStatus(): ConditionStatuses {
    function clinicalStatusMap(code: ClinicalStatus | undefined) {
      switch (code) {
        case 'active':
        case 'recurrence':
        case 'relapse':
          return 'Active';
        case 'inactive':
        case 'remission':
        case 'resolved':
          return 'Inactive';
        default:
          return '';
      }
    }

    function verificationStatusMap(code: VerificationStatus | undefined) {
      switch (code) {
        case 'confirmed':
          return 'confirmed';
        case 'unconfirmed':
        case 'provisional':
        case 'differential':
          return 'unconfirmed';
        case 'refuted':
          return 'refuted';
        case 'entered-in-error':
          return 'entered-in-error';
        default:
          return '';
      }
    }

    // What to show if lens or summary resource.
    if (this.isSummaryResource) {
      return clinicalStatusMap(this.clinicalStatusCode) || 'Unknown';
    }

    const concatenation =
      verificationStatusMap(this.verificationStatusCode) +
      clinicalStatusMap(this.clinicalStatusCode).toLowerCase();

    // What to show if patient record resource.
    switch (concatenation) {
      case 'unconfirmedactive':
        return 'Pending';
      case 'unconfirmedinactive':
      case 'refutedactive':
        return 'Unknown';
      case 'confirmedinactive':
        return 'Inactive';
      case 'confirmedactive':
        return 'Active';
      case 'refutedinactive':
        return 'Refuted';
      case 'entered-in-error':
        return 'Entered in Error';
      default:
        return 'Unknown';
    }
  }

  public get subjectID(): string {
    const [, subjectID] = this.resource.subject.reference?.split('/') || [];
    return subjectID || '';
  }

  public get verificationStatus(): string {
    return codeableConceptLabel(this.resource.verificationStatus);
  }

  public get verificationStatusCode(): VerificationStatus | undefined {
    return find(this.resource.verificationStatus?.coding, {
      system: SYSTEM_CONDITION_VERIFICATION_STATUS,
    })?.code as VerificationStatus | undefined;
  }
}

export const conditionStatuses = [
  'Active',
  'Inactive',
  'Entered in Error',
  'Pending',
  'Refuted',
  'Unknown',
] as const;

export const outsideConditionStatuses = ['Active', 'Inactive', 'Unknown'] as const;

export function knownCodings(code?: CodeableConcept) {
  const codings = compact(
    CONDITION_CODE_PREFERENCE_ORDER.map((codePreference) => {
      if (codePreference.checkForEnrichment) {
        return findCodingWithEnrichment(codePreference.system, code);
      }
      return findCoding(codePreference.system, code);
    }),
  );

  // The order of the array matters here because that is how it determines which record to keep when dupes are found.
  return uniqWith(codings, (prev, next) => prev.system === next.system);
}

export type ConditionStatuses =
  | (typeof conditionStatuses)[number]
  | (typeof outsideConditionStatuses)[number];
